import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Github, MessageSquare, Bug } from 'lucide-react';
import { useLanguage } from '../../i18n';

/**
 * 联系页面
 */
function Contact() {
  const { language } = useLanguage();

  const content = {
    zh: {
      title: '联系我们',
      subtitle: '我们很乐意听取您的反馈和建议',
      methods: [
        {
          icon: Github,
          title: 'GitHub Issues',
          description: '报告 Bug、提交功能建议或参与讨论',
          link: 'https://github.com/RongShiMr/JsonPreview/issues',
          linkText: '前往 GitHub Issues',
          recommended: true
        },
        {
          icon: MessageSquare,
          title: 'GitHub Discussions',
          description: '参与社区讨论、分享使用经验',
          link: 'https://github.com/RongShiMr/JsonPreview/discussions',
          linkText: '前往 GitHub Discussions'
        },
        {
          icon: Bug,
          title: '报告问题',
          description: '发现了 Bug？请帮助我们改进',
          steps: [
            '访问 <a href="https://github.com/RongShiMr/JsonPreview/issues" target="_blank" rel="noopener noreferrer">GitHub Issues</a>',
            '点击 "New Issue"',
            '选择 "Bug Report" 模板',
            '详细描述问题和复现步骤'
          ]
        },
        {
          icon: MessageSquare,
          title: '功能建议',
          description: '有好的想法？我们很乐意听',
          steps: [
            '访问 <a href="https://github.com/RongShiMr/JsonPreview/issues" target="_blank" rel="noopener noreferrer">GitHub Issues</a>',
            '点击 "New Issue"',
            '选择 "Feature Request" 模板',
            '描述您期望的功能'
          ]
        }
      ],
      faq: {
        title: '常见问题',
        items: [
          {
            q: '我的数据安全吗？',
            a: '完全安全！所有工具数据（JSON、时间戳等）都在您的浏览器本地处理，不会上传到服务器。'
          },
          {
            q: 'DevToolkit 是免费的吗？',
            a: '是的，DevToolkit 完全免费，没有任何功能限制或付费计划。'
          },
          {
            q: '支持哪些浏览器？',
            a: '支持所有现代浏览器：Chrome、Firefox、Safari、Edge 等。建议使用最新版本以获得最佳体验。'
          },
          {
            q: '可以离线使用吗？',
            a: 'DevToolkit 是 PWA 应用，您可以将其添加到主屏幕。部分功能支持离线使用。'
          },
          {
            q: '如何贡献代码？',
            a: 'DevToolkit 是开源项目，欢迎在 <a href="https://github.com/RongShiMr/JsonPreview" target="_blank" rel="noopener noreferrer">GitHub</a> 提交 Pull Request。'
          }
        ]
      },
      response: {
        title: '响应时间',
        content: '我们会尽快回复您的问题和建议，通常在 1-3 个工作日内。对于紧急的 Bug 报告，我们会优先处理。'
      }
    },
    en: {
      title: 'Contact Us',
      subtitle: 'We\'d love to hear your feedback and suggestions',
      methods: [
        {
          icon: Github,
          title: 'GitHub Issues',
          description: 'Report bugs, submit feature requests, or join discussions',
          link: 'https://github.com/RongShiMr/JsonPreview/issues',
          linkText: 'Go to GitHub Issues',
          recommended: true
        },
        {
          icon: MessageSquare,
          title: 'GitHub Discussions',
          description: 'Join community discussions and share your experience',
          link: 'https://github.com/RongShiMr/JsonPreview/discussions',
          linkText: 'Go to GitHub Discussions'
        },
        {
          icon: Bug,
          title: 'Report a Bug',
          description: 'Found a bug? Help us improve',
          steps: [
            'Visit <a href="https://github.com/RongShiMr/JsonPreview/issues" target="_blank" rel="noopener noreferrer">GitHub Issues</a>',
            'Click "New Issue"',
            'Select "Bug Report" template',
            'Describe the issue and reproduction steps'
          ]
        },
        {
          icon: MessageSquare,
          title: 'Feature Requests',
          description: 'Have a great idea? We\'d love to hear it',
          steps: [
            'Visit <a href="https://github.com/RongShiMr/JsonPreview/issues" target="_blank" rel="noopener noreferrer">GitHub Issues</a>',
            'Click "New Issue"',
            'Select "Feature Request" template',
            'Describe your desired feature'
          ]
        }
      ],
      faq: {
        title: 'FAQ',
        items: [
          {
            q: 'Is my data safe?',
            a: 'Absolutely! All tool data (JSON, timestamps, etc.) is processed locally in your browser and never uploaded to servers.'
          },
          {
            q: 'Is DevToolkit free?',
            a: 'Yes, DevToolkit is completely free with no feature limitations or paid plans.'
          },
          {
            q: 'Which browsers are supported?',
            a: 'All modern browsers are supported: Chrome, Firefox, Safari, Edge, etc. We recommend using the latest version for the best experience.'
          },
          {
            q: 'Can I use it offline?',
            a: 'DevToolkit is a PWA application. You can add it to your home screen and use some features offline.'
          },
          {
            q: 'How can I contribute code?',
            a: 'DevToolkit is open source. Feel free to submit Pull Requests on <a href="https://github.com/RongShiMr/JsonPreview" target="_blank" rel="noopener noreferrer">GitHub</a>.'
          }
        ]
      },
      response: {
        title: 'Response Time',
        content: 'We try to respond to questions and suggestions as quickly as possible, usually within 1-3 business days. Urgent bug reports are prioritized.'
      }
    }
  };

  const data = content[language];

  return (
    <div className="bg-[#faf9f6] dark:bg-gray-900">
      {/* Content */}
      <div className="max-w-4xl mx-auto px-6 py-12">
        {/* 页面标题 */}
        <div className="mb-12">
          <div className="flex items-center gap-3 mb-3">
            <Mail size={28} className="text-indigo-600 dark:text-indigo-400" />
            <h1 className="text-3xl font-bold text-gray-900 dark:text-white">{data.title}</h1>
          </div>
          <p className="text-gray-600 dark:text-gray-400">{data.subtitle}</p>
        </div>
        {/* Contact Methods */}
        <div className="grid md:grid-cols-2 gap-6 mb-12">
          {data.methods.map((method, index) => {
            const Icon = method.icon;
            return (
              <div
                key={index}
                className={`bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border transition-all duration-200 hover:shadow-lg ${
                  method.recommended
                    ? 'border-indigo-300 dark:border-indigo-600 ring-2 ring-indigo-100 dark:ring-indigo-900'
                    : 'border-gray-200 dark:border-gray-700'
                }`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-3 rounded-xl ${
                    method.recommended
                      ? 'bg-indigo-100 dark:bg-indigo-900/30'
                      : 'bg-gray-100 dark:bg-gray-700'
                  }`}>
                    <Icon size={24} className={method.recommended ? 'text-indigo-600 dark:text-indigo-400' : 'text-gray-600 dark:text-gray-400'} />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                      {method.title}
                      {method.recommended && (
                        <span className="ml-2 text-xs px-2 py-1 bg-indigo-100 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-full">
                          {language === 'zh' ? '推荐' : 'Recommended'}
                        </span>
                      )}
                    </h3>
                    <p className="text-gray-600 dark:text-gray-400 mb-4">{method.description}</p>
                    {method.link && (
                      <a
                        href={method.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-2 text-indigo-600 dark:text-indigo-400 hover:text-indigo-700 dark:hover:text-indigo-300 font-medium"
                      >
                        {method.linkText}
                        <ArrowLeft size={16} className="rotate-180" />
                      </a>
                    )}
                    {method.steps && (
                      <ol className="list-decimal list-inside space-y-2 text-sm text-gray-600 dark:text-gray-400 prose dark:prose-invert prose-a:text-indigo-600 dark:prose-a:text-indigo-400">
                        {method.steps.map((step, i) => (
                          <li key={i} dangerouslySetInnerHTML={{ __html: step }} />
                        ))}
                      </ol>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* FAQ */}
        <div className="bg-white dark:bg-gray-800 rounded-2xl p-8 shadow-sm border border-gray-200 dark:border-gray-700 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-6">{data.faq.title}</h2>
          <div className="space-y-6">
            {data.faq.items.map((item, index) => (
              <div key={index} className="border-b border-gray-200 dark:border-gray-700 last:border-0 pb-6 last:pb-0">
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{item.q}</h3>
                <p className="text-gray-600 dark:text-gray-400 prose dark:prose-invert prose-a:text-indigo-600 dark:prose-a:text-indigo-400" dangerouslySetInnerHTML={{ __html: item.a }} />
              </div>
            ))}
          </div>
        </div>

        {/* Response Time */}
        <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-2xl p-8 border border-indigo-200 dark:border-indigo-700 mb-8">
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">{data.response.title}</h2>
          <p className="text-gray-700 dark:text-gray-300 text-lg leading-relaxed">{data.response.content}</p>
        </div>

        {/* Back Button */}
        <div className="pt-8 border-t border-gray-200 dark:border-gray-700">
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded-lg border border-gray-300 dark:border-gray-600 transition-colors"
          >
            <ArrowLeft size={16} />
            {language === 'zh' ? '返回首页' : 'Back to Home'}
          </Link>
        </div>
      </div>
    </div>
  );
}

export default Contact;
